import React, { useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Textarea } from 'app/components/ui/textarea';
import { Download } from 'lucide-react';
import FormField from './components/FormField';
import SwitchField from './components/SwitchField';
import useOptimizedFormWatch from './hooks/useOptimizedFormWatch';

export default function DigitalFields() {
  const form = useFormContext();
  const watchedValues = useOptimizedFormWatch();

  // Memoize handlers to prevent unnecessary re-renders
  const handleDigitalDownloadChange = useCallback(
    (checked: boolean) => {
      form.setValue('is_digital_download', checked);
      // Reset related fields when disabled
      if (!checked) {
        form.setValue('digital_file_url', '');
        form.setValue('download_limit', null);
        form.setValue('download_expiry_days', null);
      }
    },
    [form],
  );

  const handleActivationKeyChange = useCallback(
    (checked: boolean) => {
      form.setValue('requires_activation_key', checked);
      if (!checked) {
        form.setValue('activation_keys', '');
      }
    },
    [form],
  );

  const handleTrackDigitalStockChange = useCallback(
    (checked: boolean) => {
      form.setValue('track_digital_stock', checked);
      if (!checked) {
        form.setValue('digital_stock_quantity', null);
      }
    },
    [form],
  );

  // Memoize conditional sections to prevent unnecessary re-renders
  const activationKeysSection = useMemo(() => {
    if (!watchedValues.requiresActivationKey) return null;

    return (
      <FormField
        label="Activation Keys"
        helpText="Enter one activation key per line. These will be automatically assigned to customers upon purchase."
        color="orange"
      >
        <Textarea
          placeholder="Enter activation keys, one per line:&#10;ABC123-DEF456-GHI789&#10;JKL012-MNO345-PQR678&#10;STU901-VWX234-YZA567"
          className="min-h-[120px] bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 rounded-lg transition-all duration-200 text-sm resize-none"
          {...form.register('activation_keys')}
        />
      </FormField>
    );
  }, [watchedValues.requiresActivationKey, form]);

  const digitalFileSection = useMemo(() => {
    if (!watchedValues.isDigitalDownload) return null;

    return (
      <FormField
        label="Digital File URL"
        helpText="Direct URL to the digital file for download"
        color="blue"
      >
        <Input
          placeholder="https://example.com/download/file.zip"
          className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg transition-all duration-200 text-sm"
          {...form.register('digital_file_url')}
        />
      </FormField>
    );
  }, [watchedValues.isDigitalDownload, form]);

  const downloadLimitsSection = useMemo(() => {
    if (!watchedValues.isDigitalDownload) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label="Download Limit"
          helpText="Maximum number of downloads allowed (leave empty for unlimited)"
          color="blue"
        >
          <Input
            type="number"
            placeholder="5"
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg transition-all duration-200 text-sm"
            {...form.register('download_limit', { valueAsNumber: true })}
          />
        </FormField>
        <FormField
          label="Download Expiry (Days)"
          helpText="Days until download links expire (leave empty for no expiry)"
          color="blue"
        >
          <Input
            type="number"
            placeholder="30"
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg transition-all duration-200 text-sm"
            {...form.register('download_expiry_days', {
              valueAsNumber: true,
            })}
          />
        </FormField>
      </div>
    );
  }, [watchedValues.isDigitalDownload, form]);

  const digitalStockSection = useMemo(() => {
    if (!watchedValues.isDigitalDownload) return null;

    return (
      <>
        <SwitchField
          label="Track Digital Stock"
          description="Enable if this digital product has limited availability"
          checked={watchedValues.trackDigitalStock}
          onChange={handleTrackDigitalStockChange}
        />
        {watchedValues.trackDigitalStock && (
          <FormField
            label="Digital Stock Quantity"
            helpText="Maximum number of digital copies available for sale"
            color="purple"
          >
            <Input
              type="number"
              placeholder="100"
              className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 rounded-lg transition-all duration-200 text-sm"
              {...form.register('digital_stock_quantity', {
                valueAsNumber: true,
              })}
            />
          </FormField>
        )}
      </>
    );
  }, [
    watchedValues.isDigitalDownload,
    watchedValues.trackDigitalStock,
    handleTrackDigitalStockChange,
    form,
  ]);

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex shrink-0 items-center justify-center">
            <Download className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Digital Product Configuration
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Configure digital download settings
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Digital Download Settings */}
        <div className="space-y-3">
          <SwitchField
            label="Is Digital Download"
            description="This product is a digital download"
            checked={watchedValues.isDigitalDownload}
            onChange={handleDigitalDownloadChange}
          />

          <SwitchField
            label="Requires Activation Key"
            description="Product requires an activation key for use"
            checked={watchedValues.requiresActivationKey}
            onChange={handleActivationKeyChange}
          />
        </div>

        {/* Activation Keys */}
        {activationKeysSection}

        {/* Digital Stock Management */}
        {digitalStockSection}

        {/* Digital File URL */}
        {digitalFileSection}

        {/* Download Limits */}
        {downloadLimitsSection}

        {/* Delivery Time for Digital */}
        <FormField
          label="Delivery Time"
          helpText="Expected time for digital product delivery (e.g., 1-20 minutes, Instant)"
          color="green"
        >
          <Input
            placeholder="e.g., 1-20 minutes, Instant, Within 1 hour"
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-green-500 rounded-lg transition-all duration-200 text-sm"
            {...form.register('digital_delivery_time')}
          />
        </FormField>

        {/* Delivery Instructions for Digital */}
        <FormField
          label="Delivery Instructions"
          helpText="Instructions shown to customers after purchase"
          color="blue"
        >
          <Input
            placeholder="e.g., Check your email for download link"
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg transition-all duration-200 text-sm"
            {...form.register('delivery_instructions')}
          />
        </FormField>
      </div>
    </div>
  );
}
